import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Seo, { SITE_URL, SITE_NAME } from "../components/Seo";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, BookOpen, Check, Clock, Eye, Lock } from "lucide-react";
import ArticlePaywall from "../components/ArticlePaywall";
import ReadingProgress from "../components/ReadingProgress";
import LevelIndicator from "../components/LevelIndicator";
import AdminReaderPreview, { readerViewLabel } from "../components/AdminReaderPreview";
import TierBadge from "../components/TierBadge";
import { Crumbs, LetterBar, ReadBadge, RetroDate, SignupHint } from "../components/CourseBits";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCourseProgress, useCoursesData } from "../lib/courseData";
import { COURSE_TYPE_LABEL, firstLetter, relatedOf, sortAlpha, sortRetros, themeOfCourse } from "../lib/courseMeta";
import { linkGlossary } from "../lib/glossary";
import GlossaryHover from "../components/GlossaryHover";
import { applyInternalLinks, renderMath } from "../lib/content";
import { safeHtml } from "../lib/sanitize";
import { useInternalLinks } from "../lib/internalLinks";
import TableOfContents from "../components/editor/TableOfContents";
const EMPTY = [];
function extractEmbed(url) {
  if (!url) return null;
  if (url.includes("/embed/")) return url;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/shorts\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}
/** Sommaire latéral : la thématique (concept) ou la frise de toutes les rétrospectives. */
function SideList({ course, theme, retros, readIds, loggedIn }) {
  if (course.category === "article") {
    return (
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">Rétrospectives</div>
        <div className="text-sm text-zinc-400 mb-3">Dans l'ordre chronologique</div>
        <ol className="relative border-l border-white/10 ml-1.5 space-y-1">
          {retros.map((e) => {
            const cur = e.id === course.id, r = loggedIn && readIds.has(e.id);
            return (
              <li key={e.id}>
                <Link to={`/cours/${e.slug}`} className={`relative flex gap-3 pl-4 pr-2 py-1.5 rounded-r text-sm ${cur ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white"}`}>
                  <span className={`absolute -left-[5px] top-3 w-2.5 h-2.5 rounded-full ${r ? "bg-emerald-400" : cur ? "bg-orange-400" : "bg-zinc-700"}`} />
                  <RetroDate course={e} className={`text-xs w-[4.5rem] shrink-0 pt-0.5 ${cur ? "text-orange-300" : "text-zinc-500"}`} />
                  <span className="leading-snug">{e.title}</span>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }
  if (!theme) return null;
  const list = theme.courses;
  const done = list.filter((x) => readIds.has(x.id)).length;
  return (
    <div>
      <Link to={`/cours/thematique/${theme.slug}`} className="group block">
        <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">Thématique</div>
        <div className="font-display font-bold leading-tight group-hover:text-orange-300">{theme.title}</div>
      </Link>
      {loggedIn && (
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-orange-400" style={{ width: `${(done / list.length) * 100}%` }} /></div>
          <span className="text-xs text-zinc-500">{done}/{list.length}</span>
        </div>
      )}
      <ol className="mt-4 space-y-0.5">
        {list.map((x, i) => {
          const cur = x.id === course.id, r = loggedIn && readIds.has(x.id);
          return (
            <li key={x.id}>
              <Link to={`/cours/${x.slug}`} className={`flex items-start gap-2.5 rounded-md px-2 py-1.5 text-sm ${cur ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white"}`}>
                <span className={`w-5 h-5 shrink-0 grid place-items-center rounded-full text-[10px] mt-px ${r ? "bg-emerald-500/20 text-emerald-300" : cur ? "bg-orange-500 text-black font-semibold" : "border border-white/15 text-zinc-500"}`}>
                  {r ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="flex-1 leading-snug">{x.title}</span>
                {x.is_locked && <Lock className="w-3 h-3 text-zinc-600 mt-1" />}
              </Link>
            </li>
          );
        })}
      </ol>
      {!loggedIn && <div className="mt-4 pt-4 border-t border-white/5"><SignupHint className="text-xs" /></div>}
    </div>
  );
}
/** Carte « précédent / suivant ». */
function NavCard({ item, label, sub, align = "left", big = false }) {
  if (!item) return <div />;
  return (
    <Link
      to={`/cours/${item.slug}`}
      className={`group block rounded-xl border p-5 ${align === "right" ? "text-right" : ""} ${big ? "border-orange-500/30 bg-orange-500/[0.04] hover:bg-orange-500/[0.08]" : "border-white/5 bg-card hover:border-white/15"}`}
    >
      <div className={`text-xs inline-flex items-center gap-1.5 ${big ? "text-orange-400" : "text-zinc-500"}`}>
        {align === "right" && <ArrowLeft className="w-3.5 h-3.5" />}{label}{align === "left" && <ArrowRight className="w-3.5 h-3.5" />}
      </div>
      <div className={`mt-2 font-display font-bold leading-snug group-hover:text-orange-300 ${big ? "text-xl" : ""}`}>{item.title}</div>
      <div className={`mt-1.5 flex flex-wrap items-center gap-3 text-xs text-zinc-500 ${align === "right" ? "justify-end" : ""}`}>
        {sub && <span>{sub}</span>}
        {item.reading_minutes && item.category !== "vocabulary" ? <span>{item.reading_minutes} min</span> : null}
        {item.is_premium && <TierBadge tier="essentiel" locked={!!item.is_locked} />}
      </div>
    </Link>
  );
}
/** Petites cartes de contenus liés. */
function RelatedGrid({ items, readIds, loggedIn, themes }) {
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {items.map((x) => (
        <Link key={x.id} to={`/cours/${x.slug}`} className="group rounded-xl border border-white/5 bg-card p-4 hover:border-orange-500/30 flex gap-4">
          {x.category === "article" && <RetroDate course={x} className="text-2xl text-orange-300/90 leading-none shrink-0" />}
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              {x.category === "concept" ? themeOfCourse(themes, x)?.title || "Concept" : COURSE_TYPE_LABEL[x.category]}
            </span>
            <span className="block mt-1 font-medium leading-snug group-hover:text-orange-300">{x.title}</span>
            {x.description && <span className="block mt-1 text-sm text-zinc-400 line-clamp-2">{x.description}</span>}
            <span className="mt-2 flex items-center gap-2">
              <LevelIndicator level={x.level} />
              {loggedIn && readIds.has(x.id) && <Check className="w-3.5 h-3.5 text-emerald-300" />}
            </span>
          </span>
        </Link>
      ))}
    </div>
  );
}
/**
 * Texte du cours. Composant mémorisé : il n'est jamais redessiné quand la page change d'état
 * (survol d'un mot, progression), sinon les liens du glossaire ajoutés au texte seraient effacés.
 */
const CourseHtml = memo(function CourseHtml({ html, terms, off, skipId, hrefs, onClick, onOver, onLeave }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    applyInternalLinks(ref.current, hrefs); // liens vers d'autres cours / analyses : adresse à jour
    if (terms.length) linkGlossary(ref.current, terms, { off, skipId });
    renderMath(ref.current); // formules
  }, [html, terms, off, skipId, hrefs]);
  return (
    <div
      ref={ref}
      className="course-content prose prose-invert prose-emerald md:prose-lg max-w-none prose-img:rounded-lg prose-headings:font-display prose-headings:font-black"
      dangerouslySetInnerHTML={{ __html: safeHtml(html) }}
      onClick={onClick}
      onMouseOver={onOver}
      onMouseLeave={onLeave}
      data-testid="course-content"
    />
  );
});
export default function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!(user && user.role === "admin");
  // Aperçu admin : "admin" = vue normale ; sinon simule un visiteur, un membre gratuit ou un abonné.
  const [previewAs, setPreviewAs] = useState("admin");
  const view = isAdmin ? previewAs : "admin";
  const previewParam =
    view === "subscriber" ? "subscriber" : view === "guest" || view === "free" ? "free" : null;
  const bodyRef = useRef(null);
  const endRef = useRef(null);
  const [hover, setHover] = useState(null);
  const { data: course, isLoading, isError } = useQuery({
    queryKey: ["course-detail", slug, previewParam],
    queryFn: async () =>
      (await api.get(`/courses/by-slug/${slug}`, { params: previewParam ? { preview_as: previewParam } : undefined })).data,
    retry: false,
  });
  const { courses, themes } = useCoursesData();
  const { hrefs } = useInternalLinks();
  const progress = useCourseProgress();
  const isPreviewing = isAdmin && previewAs !== "admin";
  const loggedIn = isPreviewing ? previewAs !== "guest" : progress.loggedIn;
  const readIds = isPreviewing ? new Set() : progress.readIds;
  const terms = useMemo(() => courses.filter((c) => c.category === "vocabulary"), [courses]);
  const countsForProgress = course && ["concept", "article"].includes(course.category);
  const isRead = !!course && readIds.has(course.id);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  // Le cours passe en « lu » quand le lecteur atteint la fin du texte (jamais pendant un aperçu admin)
  const { markRead } = progress;
  useEffect(() => {
    if (!course || !countsForProgress || course.is_locked || isPreviewing || !progress.loggedIn || isRead) return undefined;
    let done = false;
    const check = () => {
      const el = endRef.current;
      if (done || !el || el.getBoundingClientRect().top > window.innerHeight) return;
      done = true;
      markRead(course.id)
        .then((ok) => ok && toast.success("Cours terminé : ajouté à votre progression"))
        .catch(() => { done = false; });
    };
    window.addEventListener("scroll", check, { passive: true });
    const t = setTimeout(check, 1500); // cours court, entièrement visible sans défiler
    return () => { window.removeEventListener("scroll", check); clearTimeout(t); };
  }, [course, countsForProgress, isPreviewing, progress.loggedIn, isRead, markRead]);
  // Liens internes du texte (mots du glossaire, autres cours) : navigation sans rechargement
  const onContentClick = useCallback((e) => {
    const a = e.target.closest("a");
    const href = a?.getAttribute("href") || "";
    if (href.startsWith("/") && !href.startsWith("//") && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      setHover(null);
      navigate(href);
    }
  }, [navigate]);
  const onContentLeave = useCallback(() => setHover(null), []);
  const onContentOver = useCallback((e) => {
    const a = e.target.closest("a.gloss");
    if (!a) { setHover(null); return; }
    const r = a.getBoundingClientRect();
    setHover({ title: a.getAttribute("data-gloss-title"), def: a.getAttribute("data-gloss-def"), x: r.left + r.width / 2, y: r.top, top: r.top, bottom: r.bottom });
  }, []);
  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="h-8 w-2/3 bg-white/5 rounded animate-pulse mb-4" />
        <div className="h-4 w-1/3 bg-white/5 rounded animate-pulse mb-8" />
        <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-4 bg-white/5 rounded animate-pulse" />)}</div>
      </div>
    );
  }
  if (isError || !course) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display font-black text-3xl mb-3">Cours introuvable</h1>
        <p className="text-zinc-500 mb-6">Ce cours n'existe pas ou n'est plus disponible.</p>
        <Button asChild variant="outline" className="border-white/10 bg-transparent">
          <Link to="/cours"><ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux cours</Link>
        </Button>
      </div>
    );
  }
  const cat = course.category;
  const isGlossary = cat === "vocabulary";
  const theme = cat === "concept" ? themeOfCourse(themes, course) : null;
  const retros = sortRetros(courses.filter((c) => c.category === "article"));
  const related = relatedOf(course, courses);
  // Position, précédent / suivant et intitulés selon le type de contenu
  let crumbs, eyebrow, prev, next, prevLabel, nextLabel, subOf;
  if (cat === "concept" && theme) {
    const i = theme.courses.findIndex((c) => c.id === course.id);
    prev = theme.courses[i - 1]; next = theme.courses[i + 1];
    crumbs = [["Cours", "/cours"], [theme.title, `/cours/thematique/${theme.slug}`], [course.title]];
    eyebrow = `Concept · Cours ${i + 1} sur ${theme.courses.length}`;
    prevLabel = "Cours précédent"; nextLabel = "Cours suivant";
    subOf = (x) => `Cours ${theme.courses.indexOf(x) + 1} sur ${theme.courses.length}`;
  } else if (cat === "article") {
    const i = retros.findIndex((c) => c.id === course.id);
    prev = retros[i - 1]; next = retros[i + 1];
    crumbs = [["Cours", "/cours"], ["Rétrospectives", "/cours"], [course.title]];
    eyebrow = `Rétrospective${course.period ? ` · ${course.period}` : ""}`;
    prevLabel = "Rétrospective précédente"; nextLabel = "Rétrospective suivante";
    subOf = (x) => x.period;
  } else if (isGlossary) {
    const L = firstLetter(course.title);
    const sorted = sortAlpha(terms);
    const i = sorted.findIndex((c) => c.id === course.id);
    prev = sorted[i - 1]; next = sorted[i + 1];
    crumbs = [["Cours", "/cours"], ["Glossaire", "/cours"], [`Lettre ${L}`, `/cours/glossaire/${L.toLowerCase()}`], [course.title]];
    eyebrow = "Glossaire";
    prevLabel = "Définition précédente"; nextLabel = "Définition suivante";
  } else {
    crumbs = [["Cours", "/cours"], [course.title]];
    eyebrow = COURSE_TYPE_LABEL[cat] || cat;
  }
  const relRetros = related.filter((x) => x.category === "article");
  const relConcepts = related.filter((x) => x.category === "concept");
  const relTerms = related.filter((x) => x.category === "vocabulary");
  const hasSide = cat === "article" || (cat === "concept" && !!theme);
  const lastOfTheme = cat === "concept" && theme && !next;
  const header = (
    <>
      <Crumbs items={crumbs} />
      {hasSide && (
        <details className="lg:hidden mt-5 rounded-xl border border-white/5 bg-card">
          <summary className="cursor-pointer px-4 py-3 text-sm text-zinc-300">{cat === "article" ? "Toutes les rétrospectives" : "Sommaire de la thématique"}</summary>
          <div className="px-4 pb-4"><SideList course={course} theme={theme} retros={retros} readIds={readIds} loggedIn={loggedIn} /></div>
        </details>
      )}
      <div className="mt-6 sm:mt-8 text-xs uppercase tracking-[0.2em] text-orange-400">{eyebrow}</div>
      <h1 className="mt-3 font-display font-black text-3xl sm:text-[2.6rem] leading-tight max-sm:text-balance" data-testid="course-title">{course.title}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-500">
        <LevelIndicator level={course.level} className="!text-sm" />
        {!isGlossary && course.reading_minutes ? (
          <span className="inline-flex items-center gap-1" data-testid="course-reading-time"><Clock className="w-3.5 h-3.5" /> {course.reading_minutes} min de lecture</span>
        ) : null}
        {course.is_premium && <TierBadge tier="essentiel" locked={!!course.is_locked} />}
        {loggedIn && isRead && <ReadBadge />}
        <span>Par {course.author || "Side B"}</span>
      </div>
      {course.description && (
        isGlossary ? (
          <p className="mt-6 text-xl text-zinc-200 leading-relaxed border-l-2 border-orange-400 pl-5" data-testid="course-lead">{course.description}</p>
        ) : (
          <p className="mt-5 text-lg text-zinc-300 leading-relaxed" data-testid="course-lead">{course.description}</p>
        )
      )}
    </>
  );
  // Seule une rétrospective peut avoir une image (en haut du cours et dans l'aperçu des liens partagés)
  const retroImage = course.category === "article" ? course.thumbnail : null;
  return (
    <article className="min-h-screen" data-testid="course-detail">
      <Seo
        title={course.title}
        description={course.description || undefined}
        path={`/cours/${course.slug}`}
        image={retroImage || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.excerpt || course.description || undefined,
          image: retroImage ? [retroImage] : undefined,
          inLanguage: "fr",
          url: `${SITE_URL}/cours/${course.slug}`,
          provider: {
            "@type": "Organization",
            name: SITE_NAME,
            sameAs: SITE_URL,
          },
        }}
      />
      <ReadingProgress targetRef={bodyRef} color="orange" />
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-6xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez le cours comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs)}</b>
            </span>
            <button type="button" onClick={() => setPreviewAs("admin")} className="text-violet-300 hover:text-white underline-offset-4 hover:underline">
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}
      {isGlossary && <LetterBar active={firstLetter(course.title)} available={new Set(terms.map((t) => firstLetter(t.title)))} />}
      <div className={`mx-auto px-6 pt-6 sm:pt-8 pb-8 sm:pb-16 ${hasSide ? "max-w-6xl grid gap-10 lg:grid-cols-[250px_minmax(0,1fr)]" : "max-w-3xl"}`}>
        {hasSide && (
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-1">
              <SideList course={course} theme={theme} retros={retros} readIds={readIds} loggedIn={loggedIn} />
            </div>
          </aside>
        )}
        <div className="min-w-0 max-w-[720px]">
          {header}
          {retroImage && (
            <div className="mt-8 aspect-[16/9] rounded-xl border border-white/5 overflow-hidden">
              <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div ref={bodyRef} className="mt-8">
            {!course.is_locked && course.youtube_url && (
              <div className="mb-8 aspect-video rounded-xl overflow-hidden bg-black border border-white/5">
                <iframe src={extractEmbed(course.youtube_url)} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={course.title} />
              </div>
            )}
            {!course.is_locked && <TableOfContents html={course.content} toc={course.toc} />}
            {course.content && (
              // Mots du glossaire : liens automatiques (première apparition), sauf ceux désactivés pour ce cours
              <CourseHtml
                hrefs={hrefs}
                html={course.content}
                terms={terms}
                off={course.glossary_off || EMPTY}
                skipId={course.id}
                onClick={onContentClick}
                onOver={onContentOver}
                onLeave={onContentLeave}
              />
            )}
            {course.is_locked && <ArticlePaywall loggedIn={loggedIn} subject="ce cours" fade={!!course.content} />}
          </div>
          <div ref={endRef} className="h-px" />
          {course.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {course.tags.map((t) => <span key={t} className="text-xs px-2 py-1 rounded bg-white/5 text-zinc-300">#{t}</span>)}
            </div>
          )}
          {countsForProgress && !course.is_locked && !isPreviewing && (
            <div className="mt-10" data-testid="course-read-status">
              {!loggedIn ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/5 bg-card px-5 py-4">
                  <span className="text-sm text-zinc-400">Vous êtes arrivé au bout du cours.</span>
                  <SignupHint />
                </div>
              ) : isRead ? (
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] px-5 py-4 text-sm text-emerald-300">
                  <Check className="w-4 h-4" /> Cours terminé, ajouté à votre progression.
                </div>
              ) : (
                <div className="flex items-center gap-2.5 rounded-xl border border-white/5 bg-card px-5 py-4 text-sm text-zinc-500">
                  <BookOpen className="w-4 h-4" /> Ce cours sera marqué comme lu quand vous arriverez ici.
                </div>
              )}
            </div>
          )}
          {(prev || next || lastOfTheme) && (
            <div className="mt-8 grid gap-3 sm:grid-cols-[1fr_1.4fr]" data-testid="course-prev-next">
              <NavCard item={prev} label={prevLabel} sub={prev && subOf?.(prev)} align="right" />
              {next ? (
                <NavCard item={next} label={nextLabel} sub={subOf?.(next)} big />
              ) : lastOfTheme ? (
                <Link to={`/cours/thematique/${theme.slug}`} className="block rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
                  <div className="text-xs text-emerald-300 inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Dernier cours de la thématique</div>
                  <div className="mt-2 font-display font-bold text-xl">Retour à « {theme.title} »</div>
                  <div className="mt-1.5 text-xs text-zinc-500">Voir le parcours et choisir la suite</div>
                </Link>
              ) : <div />}
            </div>
          )}
          {cat === "concept" && relRetros.length > 0 && (
            <section className="mt-12 pt-8 border-t border-white/5" data-testid="course-related-retros">
              <h2 className="font-display font-bold text-lg">À revivre en rétrospective</h2>
              <RelatedGrid items={relRetros} readIds={readIds} loggedIn={loggedIn} themes={themes} />
            </section>
          )}
          {cat === "article" && relConcepts.length > 0 && (
            <section className="mt-12 pt-8 border-t border-white/5" data-testid="course-related-concepts">
              <h2 className="font-display font-bold text-lg">Les concepts pour comprendre</h2>
              <RelatedGrid items={relConcepts} readIds={readIds} loggedIn={loggedIn} themes={themes} />
            </section>
          )}
          {(() => {
            // Les autres liens : définitions (en étiquettes) et contenus non affichés au-dessus
            const shownAbove = cat === "concept" ? relRetros : cat === "article" ? relConcepts : [];
            const rest = related.filter((x) => x.category !== "vocabulary" && !shownAbove.includes(x));
            if (!rest.length && !relTerms.length) return null;
            return (
              <section className="mt-12 pt-8 border-t border-white/5" data-testid="course-related-more">
                {rest.length > 0 && (
                  <>
                    <h2 className="font-display font-bold text-lg">Pour aller plus loin</h2>
                    <RelatedGrid items={rest} readIds={readIds} loggedIn={loggedIn} themes={themes} />
                  </>
                )}
                {relTerms.length > 0 && (
                  <div className={rest.length ? "mt-8" : ""}>
                    <h2 className="font-display font-bold text-lg mb-3">Voir aussi dans le glossaire</h2>
                    <div className="flex flex-wrap gap-2">
                      {relTerms.map((x) => (
                        <Link key={x.id} to={`/cours/${x.slug}`} className="rounded-full border border-white/10 px-3 py-1.5 text-sm text-zinc-300 hover:border-orange-500/40 hover:text-orange-300">{x.title}</Link>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })()}
        </div>
      </div>
      <GlossaryHover hover={hover} />
      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} />}
    </article>
  );
}
